import { appIntl } from '@nxsys/helpers/Common';
import { PaginationType } from '@nxsys/types/models/Common';
import { ICompany } from '@nxsys/types/models/company';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { companyActs } from '../actions';
import {
  GetActivityLogCompanyListParams,
  GetBannerCompanyListParams,
  GetCompanyListParams,
  GetEmailTemplateCompanyListParams,
  GetExpenseCompanyListParams,
  GetWorkingPatternCompanyListParams,
} from '../types';
import { IExpenseCompany } from '@nxsys/types/models/expense';
import { IEmailTemplate } from '@nxsys/types/models/emailTemplate';
import { ActivityLog } from '@nxsys/types/models/activityLogs';
import { IBanner } from '@nxsys/types/models/banner';
import { IWorkingPattern } from '@nxsys/types/models/workingPattern';

export type EmployeeState = {
  companyList: {
    loading: boolean;
    list: ICompany[];
    pagination: PaginationType;
    queryParams: GetCompanyListParams | null;
  };
  expenseCompanyList: {
    loading: boolean;
    list: IExpenseCompany[];
    pagination: PaginationType;
    queryParams: GetExpenseCompanyListParams | null;
  };
  emailTemplateCompanyList: {
    loading: boolean;
    list: IEmailTemplate[];
    pagination: PaginationType;
    queryParams: GetEmailTemplateCompanyListParams | null;
  };
  activityLogCompanyList: {
    loading: boolean;
    list: ActivityLog[];
    pagination: PaginationType;
    queryParams: GetActivityLogCompanyListParams | null;
  };
  bannerCompanyList: {
    loading: boolean;
    list: IBanner[];
    pagination: PaginationType;
    queryParams: GetBannerCompanyListParams | null;
  };
  workingPatternCompanyList: {
    loading: boolean;
    list: IWorkingPattern[];
    pagination: PaginationType;
    queryParams: GetWorkingPatternCompanyListParams | null;
  };
};

const initialState: EmployeeState = {
  companyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  expenseCompanyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  emailTemplateCompanyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  activityLogCompanyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  bannerCompanyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  workingPatternCompanyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Company List Records
    builder.addCase(companyActs.getCompanyList.pending, (state) => {
      state.companyList.loading = true;
    });
    builder.addCase(companyActs.getCompanyList.fulfilled, (state, action) => {
      state.companyList.loading = false;
      state.companyList.list = action.payload.data;
      state.companyList.pagination = action.payload.pagination;
      state.companyList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(companyActs.getCompanyList.rejected, (state) => {
      state.companyList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'company.list.getError',
        }),
      });
    });

    // Get Expense List Records
    builder.addCase(companyActs.getExpenseCompanyList.pending, (state) => {
      state.expenseCompanyList.loading = true;
    });
    builder.addCase(
      companyActs.getExpenseCompanyList.fulfilled,
      (state, action) => {
        state.expenseCompanyList.loading = false;
        state.expenseCompanyList.list = action.payload.data;
        state.expenseCompanyList.pagination = action.payload.pagination;
        state.expenseCompanyList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(companyActs.getExpenseCompanyList.rejected, (state) => {
      state.expenseCompanyList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'company.expense.list.getError',
        }),
      });
    });

    // Get Email Template Company List Records
    builder.addCase(
      companyActs.getEmailTemplateCompanyList.pending,
      (state) => {
        state.emailTemplateCompanyList.loading = true;
      },
    );
    builder.addCase(
      companyActs.getEmailTemplateCompanyList.fulfilled,
      (state, action) => {
        state.emailTemplateCompanyList.loading = false;
        state.emailTemplateCompanyList.list = action.payload.data;
        state.emailTemplateCompanyList.pagination = action.payload.pagination;
        state.emailTemplateCompanyList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      companyActs.getEmailTemplateCompanyList.rejected,
      (state) => {
        state.emailTemplateCompanyList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'company.emailTemplate.list.getError',
          }),
        });
      },
    );

    // Get Activity Log Company List Records
    builder.addCase(companyActs.getActivityLogCompanyList.pending, (state) => {
      state.activityLogCompanyList.loading = true;
    });
    builder.addCase(
      companyActs.getActivityLogCompanyList.fulfilled,
      (state, action) => {
        state.activityLogCompanyList.loading = false;
        state.activityLogCompanyList.list = action.payload.data;
        state.activityLogCompanyList.pagination = action.payload.pagination;
        state.activityLogCompanyList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(companyActs.getActivityLogCompanyList.rejected, (state) => {
      state.activityLogCompanyList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'company.activityLog.list.getError',
        }),
      });
    });

    // Get Banner List Records
    builder.addCase(companyActs.getBannerCompanyList.pending, (state) => {
      state.bannerCompanyList.loading = true;
    });
    builder.addCase(
      companyActs.getBannerCompanyList.fulfilled,
      (state, action) => {
        state.bannerCompanyList.loading = false;
        state.bannerCompanyList.list = action.payload.data;
        state.bannerCompanyList.pagination = action.payload.pagination;
        state.bannerCompanyList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(companyActs.getBannerCompanyList.rejected, (state) => {
      state.bannerCompanyList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'company.banner.list.getError',
        }),
      });
    });

    // Get Working Pattern List Records
    builder.addCase(
      companyActs.getWorkingPatternCompanyList.pending,
      (state) => {
        state.workingPatternCompanyList.loading = true;
      },
    );
    builder.addCase(
      companyActs.getWorkingPatternCompanyList.fulfilled,
      (state, action) => {
        state.workingPatternCompanyList.loading = false;
        state.workingPatternCompanyList.list = action.payload.data;
        state.workingPatternCompanyList.pagination = action.payload.pagination;
        state.workingPatternCompanyList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      companyActs.getWorkingPatternCompanyList.rejected,
      (state) => {
        state.workingPatternCompanyList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'company.workingPattern.list.getError',
          }),
        });
      },
    );
  },
});

export const companyReducer = companySlice.reducer;
export const companyActions = {
  ...companySlice.actions,
  ...companyActs,
};
